import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
  Spinner,
  Toaster,
  toast,
  useTimeFormatter,
} from "@itoresearch/design-system";
import { ArrowLeftIcon } from "lucide-react";

import { useUser } from "../hooks/use-user";
import { useBoxMessage, useMarkMessageRead } from "../hooks/use-box";
import { ApiError } from "../lib/api-client";

export const Route = createFileRoute("/box/$messageId")({
  component: MessagePage,
});

function MessagePage() {
  const { user, isLoading } = useUser();
  const navigate = useNavigate();
  const { messageId } = Route.useParams();
  const messageHash = messageId;
  const message = useBoxMessage(messageHash);
  const { mutate: markMessageRead } = useMarkMessageRead(messageHash);
  const markedRead = useRef<string | null>(null);
  const formatTime = useTimeFormatter();

  useEffect(() => {
    if (!isLoading && !user) navigate({ to: "/login" });
  }, [isLoading, user, navigate]);

  // Record the read receipt once the message is on screen. Guard on the
  // message hash rather than the mutation's `isSuccess`, so navigating away
  // and back to a different unread message never skips its receipt.
  useEffect(() => {
    if (
      message.data &&
      !message.data.isRead &&
      markedRead.current !== messageHash
    ) {
      markedRead.current = messageHash;
      markMessageRead(undefined, {
        onError: (error) => {
          markedRead.current = null;
          toast.add({
            title: "Could not record read receipt",
            type: "error",
            description:
              error instanceof Error ? error.message : "Unexpected error.",
          });
        },
      });
    }
  }, [message.data, messageHash, markMessageRead]);

  if (message.isPending || isLoading)
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Spinner /> Loading message…
      </div>
    );

  // 404: not ingested, or addressed to another mailbox (never revealed).
  if (message.error)
    return (
      <div className="space-y-4">
        <Toaster />
        <BackLink />
        <Card>
          <CardHeader>
            <CardTitle>
              {message.error instanceof ApiError && message.error.status === 503
                ? "Box mail ingestion is not configured on this deployment."
                : "Message unavailable"}
            </CardTitle>
            <CardDescription>
              {message.error instanceof ApiError && message.error.status === 404
                ? "This message is not in your mailbox."
                : message.error instanceof Error
                  ? message.error.message
                  : "An unexpected error occurred."}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );

  const data = message.data;
  if (!data) return null;

  const sender = data.fromName
    ? `${data.fromName} <${data.fromAddress ?? "unknown"}>`
    : (data.fromAddress ?? "(unknown sender)");

  return (
    <div className="space-y-4">
      <Toaster />
      <BackLink />

      <div className="space-y-1">
        <h1 className="text-xl font-bold tracking-tight">
          {data.subject ?? "(no subject)"}
        </h1>
        <p className="text-sm text-muted-foreground">
          From <span className="text-foreground">{sender}</span> ·{" "}
          {formatTime(data.receivedAt)} · UID {data.uid}
        </p>
      </div>

      <Separator />

      {/* Text bodies only — untrusted email HTML is never rendered. */}
      <Card>
        <CardContent className="pt-6">
          {data.bodyText ? (
            <pre className="whitespace-pre-wrap break-words font-mono text-sm leading-relaxed">
              {data.bodyText}
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground">
              This message has no text body to display.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function BackLink() {
  return (
    <Button render={<Link to="/box" />} variant="ghost" size="sm">
      <ArrowLeftIcon data-slot="icon" /> Back to mailbox
    </Button>
  );
}
